/**
 * Operator-authored checkout form.
 *
 * Renders the field list defined on `CheckoutStrategy.formFields`. Every input
 * id is operator-chosen — UPG does not enumerate a fixed set. The submitted
 * value lands under `inputMap || id` in `createSession`'s `validator_inputs`
 * map, so an operator can bind a shopper-friendly id (`reference`) to an
 * upstream key (`amentumReferenceNumber`) without renaming the input.
 *
 * Validation: each field's optional `validator` compiles into a
 * `FieldValidatorRuntime` (memoised by JSON-serialised rule, so a deep-equal
 * rule produces the same runtime). Live errors render under each input;
 * submit stays disabled until the latest run was clean.
 *
 * No knowledge of any specific field id — every behaviour comes from the
 * declarative definition.
 */
import { useMemo, useState } from 'preact/hooks';
import {
  type CheckoutSessionDto,
  type FormFieldDefinitionDto,
  createSession,
  pickLocale,
} from './api.js';
import { buildFieldValidator, type FieldValidatorRuntime } from './field-validator.js';

export interface DynamicFormPrefill {
  /** Keyed by the field's id (NOT inputMap). Empty / missing => default value used. */
  [fieldId: string]: string | undefined;
}

export function DynamicForm(props: {
  tenant: string;
  strategy: string;
  fields: FormFieldDefinitionDto[];
  submitLabel: string;
  methodSelected: boolean;
  locale: string;
  prefill?: DynamicFormPrefill;
  onValidated: (s: CheckoutSessionDto) => void;
  onError: (msg: string) => void;
  /** Optional help-text rendered under the submit button. */
  footerHelpText?: string;
}) {
  // Sort fields by `order` (ascending) with array order as tiebreaker. We do
  // this once per field-list shape so the order survives re-renders.
  const ordered = useMemo(() => {
    return [...props.fields]
      .map((f, idx) => ({ f, idx }))
      .sort((a, b) => {
        const ao = a.f.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.f.order ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.idx - b.idx;
      })
      .map((x) => x.f);
  }, [props.fields]);

  // Seed initial values from prefill > defaultValue > '' (or 'false' for checkbox).
  const initialValues = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const f of ordered) {
      const fromPrefill = props.prefill?.[f.id];
      if (fromPrefill !== undefined) {
        out[f.id] = fromPrefill;
        continue;
      }
      if (f.defaultValue !== undefined) {
        out[f.id] = f.defaultValue;
        continue;
      }
      out[f.id] = f.type === 'checkbox' ? 'false' : '';
    }
    return out;
  }, [ordered]); // eslint-disable-line react-hooks/exhaustive-deps

  const [values, setValues] = useState<Record<string, string>>(initialValues);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [submitting, setSubmitting] = useState(false);

  // Compile each field's validator runtime once per rule shape — same trick the
  // legacy Lookup used. We key on the JSON of the rule so a deep-equal rule
  // doesn't force a fresh recompile.
  const runtimes = useMemo<Record<string, FieldValidatorRuntime>>(() => {
    const out: Record<string, FieldValidatorRuntime> = {};
    for (const f of ordered) {
      out[f.id] = buildFieldValidator(f.validator ?? {});
    }
    return out;
  }, [ordered]);

  function setValue(field: FormFieldDefinitionDto, raw: string): void {
    setValues((prev) => ({ ...prev, [field.id]: raw }));
    // Live error feedback: clear when the field is empty (don't nag while the
    // shopper is still typing); otherwise re-run the validator.
    if (raw.length === 0 || (field.type === 'checkbox' && raw === 'false')) {
      setErrors((e) => ({ ...e, [field.id]: undefined }));
      return;
    }
    const rt = runtimes[field.id];
    if (!rt) return;
    const r = rt.run(raw);
    setErrors((e) => ({ ...e, [field.id]: r.ok ? undefined : r.error }));
  }

  async function submit(e: Event): Promise<void> {
    e.preventDefault();
    if (submitting) return;

    // Run every validator. Collect errors AND the normalised value (e.g. trim)
    // so we submit what the operator's transform produced.
    const normalised: Record<string, string> = {};
    const nextErrors: Record<string, string | undefined> = {};
    let anyError = false;
    for (const f of ordered) {
      const raw = values[f.id] ?? '';
      const rt = runtimes[f.id];
      const result = rt ? rt.run(raw) : { ok: true as const, value: raw };
      if (!result.ok) {
        anyError = true;
        nextErrors[f.id] = result.error;
      }
      normalised[f.id] = result.value;
    }
    if (anyError) {
      setErrors(nextErrors);
      return;
    }

    // Build the validator_inputs payload. inputMap lets an operator bind a
    // shopper-friendly field id to an upstream-required key.
    const validatorInputs: Record<string, string> = {};
    let shopperEmail: string | undefined;
    let shopperName: string | undefined;
    for (const f of ordered) {
      const v = normalised[f.id];
      if (v === undefined || v === '') continue;
      const key = f.inputMap ?? f.id;
      validatorInputs[key] = v;
      // Heuristic: also surface a top-level shopper.email / shopper.name so
      // downstream code that expects PII at `shopper.*` still works. We treat
      // any field whose id (or inputMap) is exactly "email" / "name" as the
      // shopper email / name; otherwise we leave it in validator_inputs only.
      if (key === 'email' || f.id === 'email') shopperEmail = v;
      if (key === 'name' || f.id === 'name') shopperName = v;
    }

    setSubmitting(true);
    try {
      const s = await createSession({
        tenant: props.tenant,
        strategy_name: props.strategy,
        validator_inputs: validatorInputs,
        shopper: {
          ...(shopperEmail ? { email: shopperEmail } : {}),
          ...(shopperName ? { name: shopperName } : {}),
        },
      });
      props.onValidated(s);
    } catch (err) {
      props.onError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const hasErrors = Object.values(errors).some((m) => m !== undefined && m !== '');

  return (
    <form
      onSubmit={submit}
      class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-3 lg:space-y-4"
    >
      {ordered.map((field) => {
        const err = errors[field.id];
        return (
          <DynamicField
            key={field.id}
            field={field}
            locale={props.locale}
            value={values[field.id] ?? ''}
            // exactOptionalPropertyTypes: pass `error` only when defined.
            {...(err !== undefined ? { error: err } : {})}
            onChange={(v) => setValue(field, v)}
          />
        );
      })}
      <button
        type="submit"
        disabled={submitting || !props.methodSelected || hasErrors}
        title={
          !props.methodSelected
            ? 'Pick a payment method to continue'
            : hasErrors
              ? 'Fix the errors above'
              : undefined
        }
        class="w-full mt-2 px-4 py-2.5 bg-primary text-on-primary text-xs uppercase tracking-widest font-semibold rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Verifying…' : props.submitLabel}
      </button>
      {!props.methodSelected && (
        <p class="text-[11px] text-on-surface-variant pt-1">
          Pick a payment method above to continue.
        </p>
      )}
      {props.footerHelpText && (
        <p class="text-body-sm text-on-surface-variant pt-1">{props.footerHelpText}</p>
      )}
    </form>
  );
}

function DynamicField(props: {
  field: FormFieldDefinitionDto;
  locale: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  const { field, locale } = props;
  const label = pickLocale(field.label, locale);
  const placeholder = pickLocale(field.placeholder, locale);
  const helpText = pickLocale(field.helpText, locale);
  const hasError = Boolean(props.error);
  const inputClass = `mt-1 w-full px-3 py-2 border rounded text-body-md outline-none ${
    hasError
      ? 'border-error focus:border-error'
      : 'border-outline-variant focus:border-on-surface'
  }`;

  // Native text-like inputs map directly to `<input type="...">`. We use the
  // operator-declared `type` as the HTML type for text/email/tel/number so the
  // browser surfaces the right keyboard on mobile.
  if (
    field.type === 'text' ||
    field.type === 'email' ||
    field.type === 'tel' ||
    field.type === 'number'
  ) {
    return (
      <label class="block">
        <span class="upg-eyebrow text-on-primary-container">{label}</span>
        <input
          class={inputClass}
          value={props.value}
          onInput={(e) => props.onChange((e.currentTarget as HTMLInputElement).value)}
          placeholder={placeholder}
          type={field.type}
          aria-invalid={hasError ? 'true' : 'false'}
          required={field.required}
        />
        {hasError && (
          <p class="text-[11px] text-error mt-1" role="alert">
            {props.error}
          </p>
        )}
        {helpText && !hasError && (
          <p class="text-[11px] text-on-surface-variant mt-1">{helpText}</p>
        )}
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label class="block">
        <span class="upg-eyebrow text-on-primary-container">{label}</span>
        <select
          class={inputClass}
          value={props.value}
          onChange={(e) => props.onChange((e.currentTarget as HTMLSelectElement).value)}
          aria-invalid={hasError ? 'true' : 'false'}
          required={field.required}
        >
          {/* Empty placeholder option only when the field has no value yet and
              is not required, so the shopper has a visible "nothing selected"
              affordance. Required fields force a real choice. */}
          {!field.required && <option value="">{placeholder || '—'}</option>}
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {pickLocale(opt.label, locale)}
            </option>
          ))}
        </select>
        {hasError && (
          <p class="text-[11px] text-error mt-1" role="alert">
            {props.error}
          </p>
        )}
        {helpText && !hasError && (
          <p class="text-[11px] text-on-surface-variant mt-1">{helpText}</p>
        )}
      </label>
    );
  }

  if (field.type === 'radio') {
    return (
      <fieldset class="block">
        <legend class="upg-eyebrow text-on-primary-container">{label}</legend>
        <div class="mt-1 space-y-1.5">
          {(field.options ?? []).map((opt) => (
            <label key={opt.value} class="flex items-center gap-2 text-body-md">
              <input
                type="radio"
                name={field.id}
                value={opt.value}
                checked={props.value === opt.value}
                onChange={() => props.onChange(opt.value)}
                required={field.required}
              />
              <span>{pickLocale(opt.label, locale)}</span>
            </label>
          ))}
        </div>
        {hasError && (
          <p class="text-[11px] text-error mt-1" role="alert">
            {props.error}
          </p>
        )}
        {helpText && !hasError && (
          <p class="text-[11px] text-on-surface-variant mt-1">{helpText}</p>
        )}
      </fieldset>
    );
  }

  if (field.type === 'checkbox') {
    // Boolean represented as the string "true" / "false" — the simplest model
    // that survives JSON in `validator_inputs`. Operators get a stable value to
    // template against (`{{validator_inputs.<id>}}` → "true" / "false").
    const checked = props.value === 'true';
    return (
      <label class="flex items-start gap-2 text-body-md">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => props.onChange((e.currentTarget as HTMLInputElement).checked ? 'true' : 'false')}
          aria-invalid={hasError ? 'true' : 'false'}
          required={field.required}
          class="mt-1"
        />
        <span class="flex-1">
          <span>{label}</span>
          {hasError && (
            <span class="block text-[11px] text-error mt-1" role="alert">
              {props.error}
            </span>
          )}
          {helpText && !hasError && (
            <span class="block text-[11px] text-on-surface-variant mt-1">{helpText}</span>
          )}
        </span>
      </label>
    );
  }

  // Defensive: unknown type. Render nothing rather than crash. The admin builder
  // validates types on save, so this branch is unreachable in practice.
  return null;
}
